import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from 'store';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import Button from 'components/Buttons/Button';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import useIsDesktop from 'hooks/useIsDesktop';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { ProductType } from 'models/enums/productType';
import { amplitudelogEvent, TravelEditPaxEvent } from 'services/amplitudeService';
import { Loyalty } from 'services/loyaltiesService';
import { amplitudeEvents } from 'utils/amplitudeEvents';

import ProfileData from './components/ProfileData';
import ProfileEdit from './components/ProfileEdit';
import { Container, ButtonContainer } from './styles';

export type UserData = {
  userId: string;
  name: string;
  lastName: string;
  phoneNumber?: string;
  email: string;
  nationality?: string;
  gender?: 'Male' | 'Female';
  birthDate?: string | Date;
  birthDate_day?: string;
  birthDate_month?: string;
  birthDate_year?: string;
  documentNumber?: string;
  passportNumber?: string;
};

const USER_DATA_KEYS = [
  'userId',
  'name',
  'lastName',
  'phoneNumber',
  'email',
  'nationality',
  'gender',
  'birthDate',
  'documentNumber',
  'passportNumber',
];

type ConfirmProfileProps = {
  user: UserData;
  show: boolean;
  productType: ProductType;
  isInternationalFlight?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onSaveLoyaltyProgram?: () => void;
};

const ConfirmProfile = ({
  user,
  show,
  isInternationalFlight,
  productType,
  onClose,
  onConfirm,
  onSaveLoyaltyProgram,
}: ConfirmProfileProps) => {
  const { t } = useStoreTranslation();
  const [edit, setEdit] = useState(false);
  const isDesktop = useIsDesktop();
  const userAccount = useSelector((state: RootState) => state.user?.accountResult);
  const [selectedLoyalty, setSelectedLoyalty] = useState<Loyalty>();
  const userLoyalty = useSelector((state: RootState) => state.user?.loyalty);

  const isProfileComplete = useMemo(
    () =>
      Object.entries(user).every(([key, value]) => {
        if (key === 'passportNumber') return !!value || !isInternationalFlight;
        if (key === 'birthDate') return !!value && value !== '0001-01-01T00:00:00Z';
        if (USER_DATA_KEYS.includes(key)) return !!value;
        return true;
      }),
    [user, isInternationalFlight],
  );

  useEffect(() => {
    if (userLoyalty) {
      setSelectedLoyalty({
        loyaltyName: userLoyalty?.loyaltyName,
        loyaltyId: userLoyalty?.airlineCode,
        loyaltyNumber: userLoyalty?.loyaltyNumber,
      } as Loyalty);
    }
  }, [userLoyalty]);

  useEffect(() => {
    if (show) setEdit(false);
  }, [show]);

  const handleClickBack = () => {
    if (edit) setEdit(false);
    else onClose();
  };
  const handleClickEdit = () => {
    const event: TravelEditPaxEvent = {};

    amplitudelogEvent(amplitudeEvents.TRAVEL_EDIT_PAX, event, userAccount);

    setEdit(true);
  };

  return (
    <ApplicationOverlayContent show={show} width={1000} onClose={handleClickBack} testId="booking-confirm-profile">
      <Container>
        <Mobile>
          <AppBar
            goBack={handleClickBack}
            rightLabel={edit ? undefined : t('booking.confirmProfile.profileData.edit')}
            rightAction={handleClickEdit}
          >
            {t('booking.confirmProfile.profileData.title')}
          </AppBar>
        </Mobile>

        <Desktop>
          <ModalHeader onClose={onClose} title={t('booking.confirmProfile.profileData.title')} />
        </Desktop>

        {edit ? (
          <ProfileEdit
            user={user}
            productType={productType}
            isInternationalFlight={isInternationalFlight}
            onSave={() => setEdit(false)}
            loyaltyProgram={selectedLoyalty}
            onSaveLoyaltyProgram={onSaveLoyaltyProgram}
          />
        ) : (
          <ProfileData
            user={user}
            isInternationalFlight={isInternationalFlight}
            loyaltyProgram={selectedLoyalty}
            productType={productType}
          />
        )}

        {!edit && (
          <ButtonContainer>
            {isProfileComplete ? (
              <>
                {isDesktop && (
                  <Button onClick={handleClickEdit} invertedColor noBorder>
                    {t('booking.confirmProfile.profileData.edit')}
                  </Button>
                )}
                <Button onClick={onConfirm}>{t('booking.confirmProfile.profileData.buttons.confirm')}</Button>
              </>
            ) : (
              <>
                {isDesktop && <div />}
                <Button onClick={handleClickEdit}>{t('booking.confirmProfile.profileData.buttons.complete')}</Button>
              </>
            )}
          </ButtonContainer>
        )}
      </Container>
    </ApplicationOverlayContent>
  );
};

export default ConfirmProfile;
